import React from 'react';
import { usePagination, useSortBy, useTable } from 'react-table';
import { Pagination } from '@mui/material';
import './CustomTable.scss';
import { scrollToBottom, scrollToTop } from '../services/UtilityService';

const CustomTable = ({ columns, tblData, initialSortBy, showPagination, itemsPerPage = 10, tableId = 'customTbl',
    noHeader = false, showPage = 1, noBordered = false, isStorePageData = false, isDoublePagination = true }) => {

    const memoizedColumns = React.useMemo(() => columns, [columns]);
    // const memoizedData = React.useMemo(() => tblData, [tblData]);
    const memoizedData = React.useMemo(() => Array.isArray(tblData) ? tblData : [], [tblData]);

    const storageKey = `tableState-${tableId}`;

    const getStoredState = () => {
        try {
            const stored = sessionStorage.getItem(storageKey);
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    };

    const storedState = isStorePageData ? getStoredState() : null;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        gotoPage,
        // canPreviousPage,
        // canNextPage,
        // nextPage,
        // previousPage,
        setPageSize,
        pageOptions,
        setSortBy,
        state: { pageIndex, pageSize, sortBy },
    } = useTable(
        {
            columns: memoizedColumns,
            data: memoizedData || [],
            initialState: {
                pageIndex: storedState?.pageIndex ?? (showPage - 1),
                pageSize: storedState?.pageSize ?? itemsPerPage,
                sortBy: storedState?.sortBy || initialSortBy || []
            },
        },
        useSortBy,
        usePagination
    );

    // Save pagination state in sessionStorage
    React.useEffect(() => {
        const totalPages = Math.ceil((memoizedData?.length || 0) / pageSize);
        const maxPageIndex = Math.max(0, totalPages - 1);
        const p_index = (pageIndex > maxPageIndex) ? maxPageIndex : pageIndex;
        if (memoizedData.length > 0) {
            sessionStorage.setItem(storageKey, JSON.stringify({ pageIndex: p_index, pageSize, sortBy }));
        }
    }, [pageIndex, pageSize, sortBy, storageKey, memoizedData]);

    const handleSort = columnId => {
        const column = memoizedColumns.find(col => col.id === columnId || col.accessor === columnId);

        if (!memoizedData || memoizedData.length === 0 || column?.disableSortBy) {
            return;
        }

        const isCurrentlySorted = sortBy[0]?.id === columnId;
        const isSortedDesc = isCurrentlySorted && sortBy[0]?.desc;

        setSortBy([{ id: columnId, desc: isCurrentlySorted ? !isSortedDesc : false }]);
    };

    return (
        <div className='customTblWrapper'>
            {(showPagination && isDoublePagination && memoizedData && memoizedData.length > 0) && (
                <>
                    <div>
                        <div>
                            <Pagination
                                count={pageOptions.length}
                                page={pageIndex + 1}
                                onChange={(e, value) => {
                                    gotoPage(value - 1);
                                }}
                                variant="outlined"
                                // shape="rounded"
                                color="primary"
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'start', gap: '10px', flexWrap: 'wrap', marginTop: '5px' }}>
                        <div>
                            <select className='customInputControl' style={{ padding: '0.23rem', fontSize: '0.9rem !important' }}
                                value={pageSize}
                                onChange={e => setPageSize(Number(e.target.value))}
                            >
                                {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(size => (
                                    <option key={size} value={size}>
                                        Show ({size})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={{
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            padding: '0.23rem',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            backgroundColor: '#f9f9f9',
                            minWidth: '80px',
                            maxWidth: '150px',
                            textAlign: 'center',
                            marginBottom: '5px'
                        }}>
                            Total: {memoizedData.length || 0}
                        </div>
                    </div>
                </>
            )}

            <table id={tableId} className={`table ${noBordered ? "table-borderless" : "table-bordered"} customTbl`} {...getTableProps()}>
                {!noHeader &&
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.getHeaderGroupProps().key}>
                                {headerGroup.headers.map(column => {
                                    return (
                                        <th {...column.getHeaderProps({ onClick: () => handleSort(column.id) })} key={column.id} className={column.headerClassName} style={column.headerStyle}>
                                            {column.render('Header')}
                                            <span>
                                                {column.isSorted ? (column.isSortedDesc ? <i className="bi bi-arrow-down"></i> : <i className="bi bi-arrow-up"></i>) : ''}
                                            </span>
                                        </th>
                                    )
                                })}
                            </tr>
                        ))}
                    </thead>
                }
                {memoizedData && memoizedData.length > 0 && (
                    <tbody {...getTableBodyProps()} >
                        {page.map(row => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()} key={row.id}>
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()} key={cell.column.id} style={cell.column.style} className={cell.column.className}>{cell.render('Cell')}</td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                )}
            </table>

            {(showPagination && memoizedData && memoizedData.length > 0) && (
                <>
                    <div>
                        <div>
                            <Pagination
                                count={pageOptions.length}
                                page={pageIndex + 1}
                                onChange={(e, value) => {
                                    gotoPage(value - 1);
                                    setTimeout(() => {
                                        scrollToBottom();
                                    }, 100);
                                }}
                                variant="outlined"
                                // shape="rounded"
                                color="primary"
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'start', gap: '10px', flexWrap: 'wrap', marginTop: '5px' }}>
                        <div>
                            <select className='customInputControl' style={{ padding: '0.23rem', fontSize: '0.9rem !important' }}
                                value={pageSize}
                                onChange={e => setPageSize(Number(e.target.value))}
                            >
                                {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(size => (
                                    <option key={size} value={size}>
                                        Show ({size})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={{
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            padding: '0.23rem',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            backgroundColor: '#f9f9f9',
                            minWidth: '80px',
                            maxWidth: '150px',
                            textAlign: 'center'
                        }}>
                            Total: {memoizedData.length || 0}
                        </div>
                    </div>
                </>
            )}



        </div>
    );
}

export default CustomTable;